/**
 * Safe readers for the untyped `scenarios.data` JSONB blob.
 *
 * Scenario payloads have evolved across releases, so consumers that only need a
 * summary (advisor read-only table, listings) must tolerate several historical
 * shapes without asserting `any`. Everything here narrows from `unknown`.
 */

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function pickNumber(source: Record<string, unknown> | undefined, ...keys: string[]): number | null {
  if (!source) return null;
  for (const k of keys) {
    const v = source[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  }
  return null;
}

function pickString(source: Record<string, unknown> | undefined, ...keys: string[]): string | null {
  if (!source) return null;
  for (const k of keys) {
    const v = source[k];
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return null;
}

export type ScenarioSummary = {
  jurisdiction: "canada" | "usa";
  amount: number | null;
  purpose: string;
  stewardship: string | null;
};

export function readScenarioSummary(data: unknown): ScenarioSummary {
  const d = asRecord(data);
  const setup = asRecord(d?.setup);
  const stewardship = asRecord(d?.stewardship);

  const rating = pickString(stewardship, "rating") ?? pickString(d, "stewardshipRating");
  const score = pickNumber(stewardship, "score") ?? pickNumber(d, "stewardshipScore");

  return {
    jurisdiction: d?.jurisdiction === "usa" ? "usa" : "canada",
    amount:
      pickNumber(setup, "loanAmount") ??
      pickNumber(d, "loanAmount", "amount", "principal"),
    purpose: pickString(d, "loanPurpose", "loanName", "purpose") ?? "—",
    stewardship:
      rating && score !== null
        ? `${rating} (${score}/100)`
        : rating ?? (score !== null ? `${score}/100` : null),
  };
}
